import styled from 'styled-components';
import * as React from 'react';
import { Seo } from '../../utils/seo/Index';
import { useParams } from 'react-router';
import { Form, SectionContactStyle } from '../layout/SectionContact';
import { FormError } from '../ui/form/FormError';
import { InputRow } from '../ui/form/InputRow';
import { Textarea } from '../ui/form/TextArea';
import { Input } from '../ui/form/Input';
import { ButtonGroup } from '../ui/button/ButtonGroup';
import { Button } from '../ui/button/Button';
import { ButtonLink } from '../ui/button/ButtonLine';
import { useForm } from '../../utils/hooks/useForm';
import { SectionInfoWide } from '../layout/SectionInfoWide';
import { SectionNewsCard } from '../layout/SectionNewsCard';
import { SectionServices } from '../layout/SectionServices';

import { SectionLogo } from '../layout/SectionLogo';
import { SectionBrokers } from '../layout/SectionBrokers';
import { PageContentType } from '../../../server/global/enum';
import { printAddress } from '../../utils/helpers/printAddress';

//GraphQL
import { Query, QueryGetPageContentInputArgs, QueryGetOfficeArgs } from '../../../client/types/generated/types';
import { useMutation, useQuery } from '@apollo/client';
import { CONTENT_ITEM } from '../../../client/__graphql__/queries/page_content/getPageContent';
import { CONTACT_MUTATION } from '../../../client/__graphql__/mutations/contact';
import { OFFICE_QUERY } from '../../../client/__graphql__/queries/getOfficeLite';
import { env } from '../../__config__/env';
import { client } from '../../../client/__graphql__/clusterClient';
import { useDepartmentEmployeesQuery } from '../../__types__/generated/cluster';
import { Paragraph } from '../ui/text/Paragraph';
import { RainbowLine } from '../ui/rainbowline/RainBowLine';

const ContactWrapper = styled.div`
  margin-top: 4em;
  h3 {
    color: ${({ theme }) => theme.colors.primary};
    text-align: center;
    font-size: 27px;
  }
`;

export const FormWrapper = styled.div`
  width: 100%;
  max-width: 36em;

  .contact-logo {
    display: block;
    margin-left: auto;
    margin-right: auto;
    width: 50%;
  }
  .line {
    margin: 2em auto 2em;
    width: 100%;
  }
`;

const Address = styled.div`
  color: white;
  text-align: center;
`;

type GetPageItem = Pick<Query, 'getNdPageContent'>;
type GetPageItemVariables = QueryGetPageContentInputArgs;

const Contact: React.FC = () => {
  const params = useParams<{ urltag: string }>();
  const [posted, setPosted] = React.useState(false);

  const { data: contact } = useQuery<GetPageItem, GetPageItemVariables>(CONTENT_ITEM, {
    skip: !params.urltag,
    fetchPolicy: 'network-only',
    variables: {
      input: { urltag: params.urltag, type: PageContentType.CONTACT }
    }
  });

  const { data: employees } = useDepartmentEmployeesQuery({
    fetchPolicy: 'network-only',
    client: client,
    variables: {
      input: {
        alias: params.urltag,
        brandId: env.BRAND_ID
      }
    }
  });

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const [lead, { error, loading }] = useMutation(CONTACT_MUTATION, {
    onError: ({ graphQLErrors, networkError }) => {
      console.log(graphQLErrors, networkError);
    },
    onCompleted: ({ lead: { contact } }) => {
      if (contact && contact.success) {
        setPosted(true);
      }
    }
  });

  const { fields, getFields, handleSubmit, handleChange } = useForm({
    fields: [
      {
        type: 'text',
        name: 'name',
        placeholder: 'Ola Nordmann',
        value: '',
        label: 'Navn *',
        required: true
      },
      {
        type: 'text',
        name: 'zip',
        placeholder: 'Eks.1234',
        value: '',
        label: 'Postnummer',
        required: true
      },
      {
        type: 'tel',
        name: 'phone',
        placeholder: 'Eks. 12345678',
        value: '',
        label: 'Telefon *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      },
      {
        type: 'email',
        name: 'email',
        placeholder: 'Eks. ole@mail.no',
        value: '',
        label: 'E-post *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      },
      {
        type: 'textarea',
        name: 'comment',
        placeholder: 'Din beskjed',
        value: '',
        label: 'Kommmentar *',
        required: false
      }
    ],
    submit: () => {
      const preparedFields: any = getFields(fields);
      lead({
        variables: {
          input: {
            leadType: 'KONTAKT',
            pageType: 'KONTOR',
            identifier: params.urltag,
            name: preparedFields.name,
            zip: preparedFields.zip,
            email: preparedFields.email,
            phone: preparedFields.phone,
            comment: preparedFields.comment,
            referer: window.location.href
          }
        }
      });
    }
  });

  return (
    // Bør væres sjekket på contact.type istedenfor contact.id?
    // Verdien for type står som null i playground, enda den er lagt til
    // som tekst i databasen
    <>
      <Seo
        title={`Kontakt oss - PrivatMegleren - ${contact?.getNdPageContent?.seo?.title}`}
        description={contact?.getNdPageContent?.seo?.text ? contact?.getNdPageContent?.seo?.text : undefined}
      />

      <SectionContactStyle>
        <FormWrapper style={{}}>
          <div className="contact-logo">
            <SectionLogo
              name={contact?.getNdPageContent?.office?.name}
              style={{
                maxWidth: '280px',
                minHeight: '144px',
                textAlign: 'center'
              }}
            />
          </div>

          <SectionBrokers style={{ marginTop: '1em' }} />

          {contact?.getNdPageContent?.title ? (
            <>
              <SectionInfoWide
                style={{ paddingTop: '1em' }}
                intro={contact?.getNdPageContent?.ingress}
                title={contact?.getNdPageContent?.title}
                info={contact?.getNdPageContent?.text}
                hidebackground
              />
            </>
          ) : (
            <SectionInfoWide
              title={'Kontakt oss for en samtale'}
              intro={contact?.getNdPageContent?.ingress}
              info={contact?.getNdPageContent?.text}
              hidebackground
            />
          )}

          {!posted ? (
            <Form onSubmit={handleSubmit} style={{ paddingTop: '0em' }}>
              {error?.message ? <FormError message={error?.message} /> : null}
              {fields.map((input, index) => {
                if (input?.type === 'textarea') {
                  return (
                    <InputRow key={`field_${index}`}>
                      <Textarea
                        name={input?.name}
                        label={input?.label}
                        value={input?.value}
                        onChange={handleChange}
                        error={input?.error}
                        placeholder={input?.placeholder}
                      />
                    </InputRow>
                  );
                }
                return (
                  <InputRow key={`field_${index}`}>
                    <Input
                      name={input?.name}
                      type={input?.type}
                      label={input?.label}
                      value={input?.value}
                      onChange={handleChange}
                      error={input?.error}
                      placeholder={input?.placeholder}
                    />
                  </InputRow>
                );
              })}
              <ButtonGroup>
                <Button submit type="primary" colorTheme="gold">
                  Kontakt meg
                </Button>
                <ButtonLink href="https://privatmegleren.no/personvern" as="a" className="privacy-link-mobile ">
                  {' '}
                  Personvern
                </ButtonLink>
              </ButtonGroup>
            </Form>
          ) : (
            <ContactWrapper>
              <h3>Takk for din henvendelse!</h3>
            </ContactWrapper>
          )}
        </FormWrapper>
      </SectionContactStyle>
      {contact?.getNdPageContent?.config?.showSubpageNews ? <SectionNewsCard style={{ marginTop: '3em' }} /> : null}
      {contact?.getNdPageContent?.config?.showSubpageServices ? <SectionServices style={{ marginTop: '2em' }} /> : null}
    </>
  );
};

export default Contact;
